/**
 * Team management contract (TEAM-UX-PRD §8.1). Members, roles, invites, and the
 * team summary for the Settings → Team surface. Mutations are owner-gated in
 * the route handlers; none of these are MCP tools (agent boundary, §8.3).
 *
 * Consumed by:
 *   - excellent/app/api/team/**        (routes, createRoute)
 *   - excellent/app/settings/workspace/team/**  (client)
 */

import { z } from "zod";
import type { Operation } from "../types";

const role = z.enum(["owner", "member"]);

export const teamMemberSchema = z.object({
  userId: z.number(),
  handle: z.string(),
  displayName: z.string(),
  email: z.string().nullable(),
  role,
  deviceCount: z.number(),
  lastSeenAt: z.string().nullable(),
  createdAt: z.string(),
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

export const teamInviteSchema = z.object({
  id: z.number(),
  uid: z.string(),
  handle: z.string().nullable(),
  displayName: z.string().nullable(),
  role,
  status: z.enum(["pending", "expired", "consumed", "revoked"]),
  createdAt: z.string(),
  expiresAt: z.string(),
});
export type TeamInvite = z.infer<typeof teamInviteSchema>;

export const teamSummarySchema = z.object({
  memberCount: z.number(),
  ownerCount: z.number(),
  deviceCount: z.number(),
  openInviteCount: z.number(),
  hasFleetKey: z.boolean(),
  primaryUrl: z.string().nullable(),
  /** Is the caller the workspace owner (controls write affordances)? */
  callerIsOwner: z.boolean(),
  /** Replica vs single-player (drives the on-ramp vs dashboard view). */
  replica: z.boolean(),
});
export type TeamSummaryResponse = z.infer<typeof teamSummarySchema>;

const summaryOp: Operation<void, void, void, TeamSummaryResponse> = {
  method: "GET",
  path: () => "/api/team/summary",
  response: teamSummarySchema,
};

const membersOp: Operation<void, void, void, { members: TeamMember[] }> = {
  method: "GET",
  path: () => "/api/team/members",
  response: z.object({ members: z.array(teamMemberSchema) }),
};

const invitesOp: Operation<void, void, void, { invites: TeamInvite[] }> = {
  method: "GET",
  path: () => "/api/team/invites",
  response: z.object({ invites: z.array(teamInviteSchema) }),
};

const createInviteBody = z.object({
  handle: z.string().max(64).optional(),
  displayName: z.string().max(120).optional(),
  role: role.optional(),
  ttlHours: z.number().int().min(1).max(24 * 30).optional(),
});
const createInviteResponse = z.object({
  invite: teamInviteSchema,
  joinBundle: z.string(),
  joinLink: z.string(),
  hasToken: z.boolean(),
  /** The secure invite link (code + issuer key in the fragment; no token) —
   *  present when this node can broker. Prefer it over joinLink. */
  secureLink: z.string().nullable(),
});
const createInviteOp: Operation<
  void,
  void,
  z.infer<typeof createInviteBody>,
  z.infer<typeof createInviteResponse>
> = {
  method: "POST",
  path: () => "/api/team/invites/create",
  body: createInviteBody,
  response: createInviteResponse,
  invalidates: [invitesOp, summaryOp],
};

const okResult = z.object({ ok: z.boolean(), reason: z.string().optional() });

const revokeInviteBody = z.object({ inviteId: z.number().int() });
const revokeInviteOp: Operation<
  void,
  void,
  z.infer<typeof revokeInviteBody>,
  z.infer<typeof okResult>
> = {
  method: "POST",
  path: () => "/api/team/invites/revoke",
  body: revokeInviteBody,
  response: okResult,
  invalidates: [invitesOp, summaryOp],
};

const setRoleBody = z.object({ userId: z.number().int(), role });
const setRoleOp: Operation<
  void,
  void,
  z.infer<typeof setRoleBody>,
  z.infer<typeof okResult>
> = {
  method: "POST",
  path: () => "/api/team/members/set-role",
  body: setRoleBody,
  response: okResult,
  invalidates: [membersOp, summaryOp],
};

const removeMemberBody = z.object({ userId: z.number().int() });
const removeMemberOp: Operation<
  void,
  void,
  z.infer<typeof removeMemberBody>,
  z.infer<typeof okResult>
> = {
  method: "POST",
  path: () => "/api/team/members/remove",
  body: removeMemberBody,
  response: okResult,
  invalidates: [membersOp, summaryOp],
};

// Consume an invite for the just-registered caller — applies the invite's role
// and marks it single-use (TEAM-UX-PRD §5.4). Self-service: the authenticated
// joiner acts on their own membership, so it's not owner-gated.
const consumeInviteBody = z.object({ token: z.string().min(1) });
const consumeInviteOp: Operation<
  void,
  void,
  z.infer<typeof consumeInviteBody>,
  z.infer<typeof okResult>
> = {
  method: "POST",
  path: () => "/api/team/invites/consume",
  body: consumeInviteBody,
  response: okResult,
  invalidates: [membersOp, invitesOp, summaryOp],
};

// Flow D auto-consume: the just-registered joiner consumes a pending invite
// stashed by `sync join` on their first authenticated Team load. No body — the
// token lives in a local file (TEAM-UX-PRD §5.4). A dedicated mutation (not the
// polled summary read) so a GET never writes. `consumed` is true only when a
// pending token was present, letting the client revalidate its summary.
const consumePendingResponse = z.object({ ok: z.boolean(), consumed: z.boolean() });
const consumePendingOp: Operation<
  void,
  void,
  void,
  z.infer<typeof consumePendingResponse>
> = {
  method: "POST",
  path: () => "/api/team/invites/consume-pending",
  response: consumePendingResponse,
  invalidates: [membersOp, invitesOp, summaryOp],
};

export const teamManage = {
  summary: summaryOp,
  consumePending: consumePendingOp,
  members: membersOp,
  invites: invitesOp,
  createInvite: createInviteOp,
  revokeInvite: revokeInviteOp,
  setRole: setRoleOp,
  removeMember: removeMemberOp,
  consumeInvite: consumeInviteOp,
};
